import React, { FormEvent, ReactElement, useEffect, useState } from 'react'
import { Box, Button, Flex, FormControl, Heading, Select, Spinner } from '@chakra-ui/react'
import { SNPDataTable } from '../components/dataTable'
import { graphQLTableDataInterface, keyValue, keyValueObject, QueryKeyTypes } from '../lib/types'
import { useLoadQuery } from '../customHooks/graphQLHooks'
import { ErrorMessage } from '../components/ErrorMessage'
import { useQuery } from '@apollo/client'
import { GET_ALL_TYPES } from '../queries/queries'
import { Header } from '../components/Header'
import { QuickSelections } from './homePage/QuickSelection'

export const Admin = (): JSX.Element => {
  const [tableType, setTableType] = useState<QueryKeyTypes>('Elements')
  const [loading, setLoading] = useState<boolean>(true)
  const [tableData, setTableData] = useState<keyValue[] | undefined>([])
  const customData: graphQLTableDataInterface | undefined = useLoadQuery({
    query: tableType
  })
  const optionsArray: QueryKeyTypes[] = [
    'Element Materials',
    'Element Material Conf',
    'Element Orientations',
    'Element Types',
    'Element Mod Types',
    'Element Mods',
    'Elements',
    // 'Element Notes',
    'Product Types',
    'Product Categories',
    'Product Sub Categories',
    'Product Manufacturers',
    'Product Brands',
    'Product Industries',
    'Product Installation Types',
    'Product Installation Instruction',
    'Product Matching List',
    'Product Accessory Match',
    'Products',
    // 'Product Instructions',
    // 'Product Notes',
    'Report Status',
    'Report Types',
    'Report Facilities',
    'Report Publishing',
    'Reporting Standards',
    'Reports',
    'Service Types',
    'Service Categories',
    'Service Sub Categories',
    'Service Materials',
    'Service Manufacturers',
    'Service Brands',
    'Service Industries',
    'Service Standards',
    'Service Standard Match',
    'Service Add Types',
    'Service Add Manufacturers',
    'Service Add Brands',
    'Service Add Groups',
    'Service Adds',
    'Services',
    'Service Notes',
    'Fitting Types',
    'Fixings',
    'Component Products',
    'Service Configurations',
    'Applications',
    'Industry Categories',
    'Sealing Methods',
    'Fixing Tested',
    'Fixing Categories',
    'Fixing Groups',
    'Fixing Match List',
    'Systems',
    'System Note Types',
    'System Notes',
    'System Notes Match',
    'Document Types',
    'Units',
    'Document Versions',
    'Dimension Types',
    'FRL Standards',
    'FAQs',
    'Email Inquiries',
    'Home Page Tiles',
    'Quality Certificates',
    'SNAP Users',
    'FMP Projects'
  ]

  useEffect(() => {
    setLoading(customData.loading)
    if (customData.data && !customData.loading) {
      setTableData(customData.data)
    }
  }, [customData])

  const { data: allTypes, loading: allTypesLoading, error: allTypesError } = useQuery(GET_ALL_TYPES, { errorPolicy: 'all' }) as { data: keyValue; loading: boolean; error: any }

  return (
    <Box>
      <Header />
      <Flex align="center" borderBottom="2px" borderColor="gray.200">
        <Box data-name="quick-selections-container">
          <QuickSelections
            onClickCallBack={newTableType => {
              setLoading(true)
              setTableData(undefined)
              setTableType(newTableType)
            }}
            tableType={tableType}
          />
        </Box>
        {!customData.error && (
          <Box data-name="table-selector-container">
            <Flex align="center" mx="6" my="4">
              <Heading as="h4" size="sm">
                Tables
              </Heading>
              {loading && <Spinner size="sm" ml="3" color="primary" />}
            </Flex>
            <FormControl w="250px" m="4" data-name="table-selector-form-control">
              <Select
                h="34px"
                fontSize="12px"
                value={tableType}
                isDisabled={loading}
                onChange={(e: FormEvent) => {
                  setLoading(true)
                  const element = e.target as HTMLSelectElement
                  const value: QueryKeyTypes = element.value as QueryKeyTypes

                  setTableData(undefined)
                  setTableType(value)
                }}
              >
                {optionsArray.map(
                  (key: QueryKeyTypes): ReactElement => (
                    <option key={`element_option_${key}`} value={key} data-name={`table-selector-option-${key}`}>
                      {key}
                    </option>
                  )
                )}
              </Select>
            </FormControl>
          </Box>
        )}
        <Box data-name="page-reload-container" ml="auto" mr="6">
          <Button
            size="sm"
            colorScheme="green"
            onClick={() => {
              window.location.reload()
            }}
          >
            Reload Page
          </Button>
        </Box>
      </Flex>
      {!customData.error && (
        <>
          {loading || allTypesLoading ? (
            <Flex m="8" alignItems="center" justifyContent="center" flexDirection="column">
              <Spinner size="xl" color="primary" thickness="4px" />
              <Box mt="4" fontSize="lg" color="gray.600">
                {loading ? `Loading ${tableType} data...` : 'Loading application data...'}
              </Box>
            </Flex>
          ) : (
            allTypes && (
              <>
                {tableData && tableData?.length > 0 ? (
                  <SNPDataTable title={tableType} data={tableData} allTypes={allTypes as keyValueObject} />
                ) : (
                  <Flex m="8" alignItems="center" justifyContent="center" flexDirection="column">
                    <Box fontSize="lg" color="gray.600">
                      No {tableType} data found
                    </Box>
                  </Flex>
                )}
              </>
            )
          )}
        </>
      )}
      {customData?.error && <ErrorMessage errorMessage={customData.error.message ?? 'error'} reloadPage />}
    </Box>
  )
}
